import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { Loader2, MessageCircle, Phone, Mail, Maximize, Minimize, ShieldOff, WifiOff, PauseCircle } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { painelTvService } from '../services/painelTvService';
import { getFileUrl, getInitials } from '../utils/dashboardHelpers';
import { PainelTvResponse, PainelTicket, AtendenteCarga, CanalTicket } from '../types/operacoes';

// Painel para TV de monitoramento: público (sem login), autenticado pelo token da URL.
// Sem cliques: tudo atualiza sozinho (socket + recarga periódica de segurança).

type Urgencia = 'ok' | 'warn' | 'crit';
type Tom = 'default' | 'warn' | 'accent' | 'ok' | 'crit';

// Mesmos limites do Painel de Operações
const THRESH_PENDENTE = { warn: 10 * 60 * 1000, crit: 20 * 60 * 1000 };
const THRESH_ATENDIMENTO = { warn: 15 * 60 * 1000, crit: 30 * 60 * 1000 };

const RECARGA_PERIODICA_MS = 30 * 1000;
const INTERVALO_MINIMO_RECARGA_MS = 3 * 1000;

const CANAL_LABEL: Record<CanalTicket, string> = {
  EVOLUTION: 'WhatsApp',
  UAZAPI: 'WhatsApp',
  META: 'Meta',
  DESCONHECIDO: 'Canal'
};

const URGENCY_BORDER: Record<Urgencia, string> = {
  ok: 'border-l-emerald-500',
  warn: 'border-l-amber-500',
  crit: 'border-l-red-500'
};

const URGENCY_TEXT: Record<Urgencia, string> = {
  ok: 'text-emerald-400',
  warn: 'text-amber-400',
  crit: 'text-red-400'
};

const TOM_TEXT: Record<Tom, string> = {
  default: 'text-gray-100',
  warn: 'text-amber-400',
  accent: 'text-sky-400',
  ok: 'text-emerald-400',
  crit: 'text-red-400'
};

const STATUS_DOT: Record<string, string> = {
  online: 'bg-emerald-500',
  offline: 'bg-zinc-600',
  ausente: 'bg-amber-500'
};

const STATUS_LABEL: Record<string, string> = {
  online: 'Online',
  offline: 'Offline',
  ausente: 'Ausente'
};

const STATUS_ORDEM: Record<string, number> = { online: 0, ausente: 1, offline: 2 };

function urgencia(ms: number, thresh: { warn: number; crit: number }): Urgencia {
  if (ms >= thresh.crit) return 'crit';
  if (ms >= thresh.warn) return 'warn';
  return 'ok';
}

// Desconta a pausa "aguardando ação do cliente" em andamento (cronômetro congelado);
// pausas já encerradas o backend desconta adiantando statusAlteradoEm.
function decorrido(ticket: PainelTicket): number {
  const fim = ticket.aguardandoClienteDesde ? new Date(ticket.aguardandoClienteDesde).getTime() : Date.now();
  return fim - new Date(ticket.statusAlteradoEm).getTime();
}

// Ticket pausado aguardando o cliente não conta como atrasado
function urgenciaTicket(ticket: PainelTicket, thresh: { warn: number; crit: number }): Urgencia {
  return ticket.aguardandoClienteDesde ? 'ok' : urgencia(decorrido(ticket), thresh);
}

function fmtElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  if (d > 0) return `${d}d ${pad(h)}:${pad(m)}:${pad(sec)}`;
  if (h > 0) return `${h}:${pad(m)}:${pad(sec)}`;
  return `${m}:${pad(sec)}`;
}

function fmtDuracaoSegundos(seg: number | null): string {
  if (seg === null || seg === undefined) return '—';
  return fmtElapsed(seg * 1000);
}

function fmtAvaliacao(nota: number | null): string {
  if (nota === null || nota === undefined) return '—';
  return nota.toFixed(1);
}

type Estado = 'carregando' | 'ok' | 'revogado' | 'erro';

const PainelTvPage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const [data, setData] = useState<PainelTvResponse | null>(null);
  const [estado, setEstado] = useState<Estado>('carregando');
  const [conectado, setConectado] = useState(true);
  const [agora, setAgora] = useState(() => new Date());

  // ── Carga dos dados (com limite de frequência: no máximo 1 a cada 3s) ──
  const ultimaCargaRef = useRef(0);
  const agendadaRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const carregar = useCallback(async () => {
    ultimaCargaRef.current = Date.now();
    try {
      const resposta = await painelTvService.getPainel(token);
      setData(resposta);
      setEstado('ok');
    } catch (error: any) {
      if (error?.response?.status === 401) {
        setEstado('revogado');
      } else {
        // Mantém os últimos dados na tela; só mostra erro se nunca carregou
        setEstado((atual) => (atual === 'carregando' ? 'erro' : atual));
      }
    }
  }, [token]);

  const carregarComLimite = useCallback(() => {
    if (agendadaRef.current) return;
    const espera = Math.max(0, INTERVALO_MINIMO_RECARGA_MS - (Date.now() - ultimaCargaRef.current));
    agendadaRef.current = setTimeout(() => {
      agendadaRef.current = null;
      carregar();
    }, espera);
  }, [carregar]);

  useEffect(() => {
    carregar();
    const id = setInterval(carregar, RECARGA_PERIODICA_MS);
    return () => {
      clearInterval(id);
      if (agendadaRef.current) clearTimeout(agendadaRef.current);
    };
  }, [carregar]);

  // ── Socket: só recebe um aviso "algo mudou" e recarrega ──
  useEffect(() => {
    if (!token || estado === 'revogado') return;
    const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/api$/, '');
    const socket: Socket = io(baseUrl, {
      auth: { painelTvToken: token },
      transports: ['polling', 'websocket'],
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 15000
    });

    socket.on('connect', () => {
      setConectado(true);
      carregarComLimite();
    });
    socket.on('disconnect', () => setConectado(false));
    socket.on('connect_error', (err) => {
      setConectado(false);
      if (err?.message === 'painel_tv_token_invalido') {
        setEstado('revogado');
        socket.disconnect();
      }
    });
    socket.on('painel_tv_atualizar', carregarComLimite);
    socket.on('painel_tv_revogado', () => {
      setEstado('revogado');
      socket.disconnect();
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [token, estado === 'revogado', carregarComLimite]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Relógio (também move os cards entre as colunas de cor) ──
  useEffect(() => {
    const id = setInterval(() => setAgora(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // ── Mantém a tela da TV ligada (Wake Lock, quando o navegador suporta) ──
  useEffect(() => {
    let lock: any = null;
    const pedir = async () => {
      try {
        if ('wakeLock' in navigator && document.visibilityState === 'visible') {
          lock = await (navigator as any).wakeLock.request('screen');
        }
      } catch {
        // Sem suporte ou negado: segue normalmente
      }
    };
    pedir();
    document.addEventListener('visibilitychange', pedir);
    return () => {
      document.removeEventListener('visibilitychange', pedir);
      lock?.release?.().catch(() => undefined);
    };
  }, []);

  // ── Tela cheia + cursor/botão somem após alguns segundos sem mexer o mouse ──
  const [telaCheia, setTelaCheia] = useState(false);
  const [mouseAtivo, setMouseAtivo] = useState(true);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const mexeu = () => {
      setMouseAtivo(true);
      clearTimeout(timer);
      timer = setTimeout(() => setMouseAtivo(false), 4000);
    };
    const mudouTelaCheia = () => setTelaCheia(!!document.fullscreenElement);
    mexeu();
    window.addEventListener('mousemove', mexeu);
    document.addEventListener('fullscreenchange', mudouTelaCheia);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('mousemove', mexeu);
      document.removeEventListener('fullscreenchange', mudouTelaCheia);
    };
  }, []);

  const alternarTelaCheia = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => undefined);
    else document.documentElement.requestFullscreen().catch(() => undefined);
  };

  if (estado === 'revogado' || !token) {
    return (
      <TelaAviso
        icone={<ShieldOff className="h-14 w-14 text-red-400" />}
        titulo="Acesso ao painel revogado"
        texto="Este link não é mais válido. Peça ao administrador o novo link em Configurações → Painel TV."
      />
    );
  }

  if (estado === 'erro') {
    return (
      <TelaAviso
        icone={<WifiOff className="h-14 w-14 text-amber-400" />}
        titulo="Não foi possível carregar o painel"
        texto="Tentando novamente automaticamente…"
      />
    );
  }

  if (estado === 'carregando' || !data) {
    return (
      <div className="dark h-[100dvh] bg-zinc-950 flex items-center justify-center text-gray-400">
        <Loader2 className="h-10 w-10 animate-spin" />
      </div>
    );
  }

  const pendentes = data.pendentes;
  const atendimento = data.atendimento;
  const aguardando = data.aguardando || [];

  const esperaMediaMs = pendentes.length ? pendentes.reduce((acc, t) => acc + decorrido(t), 0) / pendentes.length : null;
  const pendentesCriticos = pendentes.filter((t) => urgenciaTicket(t, THRESH_PENDENTE) === 'crit').length;

  const atendimentoPorUrgencia: Record<Urgencia, PainelTicket[]> = { crit: [], warn: [], ok: [] };
  for (const t of atendimento) atendimentoPorUrgencia[urgenciaTicket(t, THRESH_ATENDIMENTO)].push(t);

  const atendentesOnline = data.atendentes.filter((a) => a.status === 'online').length;
  const equipe = [...data.atendentes].sort((a, b) => {
    if (a.ticketsEmAtendimento !== b.ticketsEmAtendimento) return b.ticketsEmAtendimento - a.ticketsEmAtendimento;
    const sa = STATUS_ORDEM[a.status || 'offline'] ?? 2;
    const sb = STATUS_ORDEM[b.status || 'offline'] ?? 2;
    if (sa !== sb) return sa - sb;
    return a.nome.localeCompare(b.nome, 'pt-BR');
  });

  return (
    // Até xl (celular/tablet) a página rola normalmente; a partir de xl (TV/monitor) ocupa exatamente a tela
    <div className={`dark min-h-[100dvh] xl:h-[100dvh] xl:overflow-hidden bg-zinc-950 text-gray-100 flex flex-col p-3 sm:p-4 gap-3 ${mouseAtivo ? '' : 'xl:cursor-none'}`}>
      {/* Cabeçalho */}
      <header className="flex items-center gap-3 sm:gap-4 flex-none">
        <div className="min-w-0">
          <div className="text-lg sm:text-xl font-bold truncate">{data.empresa?.nome || 'Painel de atendimento'}</div>
          <div className="text-[11px] sm:text-xs uppercase tracking-wider text-gray-500 truncate">Painel de atendimento · ao vivo</div>
        </div>
        {!conectado && (
          <span className="hidden sm:flex items-center gap-1.5 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-md px-2 py-1">
            <WifiOff className="h-3.5 w-3.5" /> Reconectando…
          </span>
        )}
        <div className="ml-auto flex items-center gap-4 flex-none">
          <div className="text-right">
            <div className="text-xl sm:text-3xl font-bold tabular-nums leading-none">
              {agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
            <div className="hidden sm:block text-xs text-gray-500 capitalize">
              {agora.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })}
            </div>
          </div>
          <button
            onClick={alternarTelaCheia}
            title={telaCheia ? 'Sair da tela cheia' : 'Tela cheia'}
            className={`hidden md:flex h-10 w-10 items-center justify-center rounded-lg border border-zinc-800 text-gray-400 hover:text-white hover:bg-zinc-800 transition-opacity ${mouseAtivo ? 'opacity-100' : 'opacity-0'}`}
          >
            {telaCheia ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
          </button>
        </div>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-1 xl:grid-cols-[4fr_8fr] gap-3 flex-none">
        <GrupoKpi titulo="Ao vivo" live>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <KpiTile destaque label="Pendentes" value={pendentes.length} tone={pendentesCriticos > 0 ? 'crit' : 'warn'}
              sub={pendentesCriticos > 0 ? `${pendentesCriticos} parado${pendentesCriticos > 1 ? 's' : ''} há muito tempo` : 'aguardando técnico'} />
            <KpiTile destaque label="Atendendo" value={atendimento.length} tone={atendimentoPorUrgencia.crit.length > 0 ? 'crit' : 'accent'}
              sub={atendimentoPorUrgencia.crit.length > 0 ? `${atendimentoPorUrgencia.crit.length} parado${atendimentoPorUrgencia.crit.length > 1 ? 's' : ''} há muito tempo` : 'com técnico'} />
            <KpiTile destaque label="Espera média" value={esperaMediaMs !== null ? fmtElapsed(esperaMediaMs) : '—'}
              tone={esperaMediaMs === null ? 'default' : ({ ok: 'default', warn: 'warn', crit: 'crit' } as const)[urgencia(esperaMediaMs, THRESH_PENDENTE)]}
              sub="dos pendentes" />
            <KpiTile destaque label="Online" value={`${atendentesOnline}/${data.atendentes.length}`} tone="ok" sub="atendentes" />
          </div>
        </GrupoKpi>
        <GrupoKpi titulo="Período · Hoje">
          <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-2">
            <KpiTile label="Atendimentos" value={data.totalAtendimentosPeriodo} tone="default" sub="total" />
            <KpiTile label="Resolvidos" value={data.resolvidosPeriodo} tone="ok" sub="finalizados" />
            <KpiTile label="Ativas" value={data.demandaAtivaPeriodo} tone="accent" sub="empresa iniciou" />
            <KpiTile label="Receptivas" value={data.demandaReceptivaPeriodo} tone="accent" sub="cliente iniciou" />
            <KpiTile label="TME" value={fmtDuracaoSegundos(data.tmeSegundos)} tone="default" sub="tempo médio espera" />
            <KpiTile label="TMA" value={fmtDuracaoSegundos(data.tmaSegundos)} tone="default" sub="tempo médio atend." />
            <KpiTile label="Avaliação" value={fmtAvaliacao(data.avaliacaoMediaPeriodo)} tone="warn" sub="nota média" />
            <KpiTile label="Novos contatos" value={data.novosContatosPeriodo} tone="ok" sub="cadastrados" />
          </div>
        </GrupoKpi>
      </div>

      {/* Quadro: pendentes | em atendimento (vermelho · amarelo · verde) | equipe */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,3fr)_minmax(0,1fr)_minmax(0,0.9fr)] gap-3 xl:flex-1 xl:min-h-0">
        <Painel titulo="Pendentes" contador={pendentes.length} subtitulo="Sem técnico · mais tempo parado no topo">
          <ListaTickets tickets={pendentes} thresh={THRESH_PENDENTE} vazio="Nenhum pendente" />
        </Painel>

        <Painel className="lg:col-span-2 lg:order-3 xl:col-span-1 xl:order-none" titulo="Em atendimento" contador={atendimento.length} subtitulo="Com técnico · separado pelo tempo em atendimento">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 xl:h-full xl:min-h-0">
            <SubColuna cor="crit" titulo="Crítico" faixa="acima de 30 min" tickets={atendimentoPorUrgencia.crit} />
            <SubColuna cor="warn" titulo="Atenção" faixa="15 a 30 min" tickets={atendimentoPorUrgencia.warn} />
            <SubColuna cor="ok" titulo="Normal" faixa="até 15 min" tickets={atendimentoPorUrgencia.ok} />
          </div>
        </Painel>

        <Painel className="lg:order-2 xl:order-none" titulo="Aguardando retorno" contador={aguardando.length} subtitulo="Aguardando retorno do contato">
          <ListaTickets tickets={aguardando} thresh={THRESH_ATENDIMENTO} vazio="Nenhum aguardando" />
        </Painel>

        <Painel className="lg:order-2 xl:order-none" titulo="Equipe" contador={`${atendentesOnline}/${equipe.length} online`} subtitulo="Chats em atendimento agora">
          <ListaEquipe atendentes={equipe} />
        </Painel>
      </div>
    </div>
  );
};

const TelaAviso: React.FC<{ icone: React.ReactNode; titulo: string; texto: string }> = ({ icone, titulo, texto }) => (
  <div className="dark h-[100dvh] bg-zinc-950 text-gray-100 flex flex-col items-center justify-center gap-4 p-6 text-center">
    {icone}
    <h1 className="text-3xl font-bold">{titulo}</h1>
    <p className="text-lg text-gray-400 max-w-xl">{texto}</p>
  </div>
);

const GrupoKpi: React.FC<{ titulo: string; live?: boolean; children: React.ReactNode }> = ({ titulo, live, children }) => (
  <div className="rounded-xl bg-zinc-900 border border-zinc-800 p-2.5">
    <div className="flex items-center gap-1.5 px-0.5 mb-2 text-xs uppercase tracking-wide font-semibold text-gray-400">
      {live && <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />}
      {titulo}
    </div>
    {children}
  </div>
);

const KpiTile: React.FC<{ label: string; value: React.ReactNode; tone: Tom; sub: string; destaque?: boolean }> = ({ label, value, tone, sub, destaque }) => (
  <div className="rounded-lg bg-zinc-800/60 border border-zinc-800 px-2.5 py-1.5 min-w-0">
    <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold truncate" title={label}>{label}</div>
    <div className={`${destaque ? 'text-2xl sm:text-3xl' : 'text-xl sm:text-2xl'} font-bold tabular-nums leading-tight truncate ${TOM_TEXT[tone]}`}>{value}</div>
    <div className="text-[11px] text-gray-500 truncate" title={sub}>{sub}</div>
  </div>
);

const Painel: React.FC<{ titulo: string; contador: React.ReactNode; subtitulo: string; className?: string; children: React.ReactNode }> = ({ titulo, contador, subtitulo, className = '', children }) => (
  <section className={`flex flex-col min-h-0 rounded-xl bg-zinc-900 border border-zinc-800 p-3 ${className}`}>
    <div className="flex items-center gap-2 flex-none">
      <h2 className="font-bold text-lg">{titulo}</h2>
      <span className="text-xs font-semibold bg-zinc-800 text-gray-300 rounded-md px-2 py-0.5">{contador}</span>
    </div>
    <p className="text-xs text-gray-500 mb-2 flex-none truncate">{subtitulo}</p>
    <div className="xl:flex-1 xl:min-h-0">{children}</div>
  </section>
);

const SUB_COLUNA_ESTILO: Record<Urgencia, { caixa: string; titulo: string; ponto: string }> = {
  crit: { caixa: 'bg-red-500/10 border-red-500/40', titulo: 'text-red-400', ponto: 'bg-red-500 animate-pulse' },
  warn: { caixa: 'bg-amber-500/10 border-amber-500/40', titulo: 'text-amber-400', ponto: 'bg-amber-500' },
  ok: { caixa: 'bg-emerald-500/10 border-emerald-500/40', titulo: 'text-emerald-400', ponto: 'bg-emerald-500' }
};

const SubColuna: React.FC<{ cor: Urgencia; titulo: string; faixa: string; tickets: PainelTicket[] }> = ({ cor, titulo, faixa, tickets }) => {
  const estilo = SUB_COLUNA_ESTILO[cor];
  return (
    <div className={`flex flex-col min-h-0 rounded-lg border ${estilo.caixa} p-2`}>
      <div className="flex items-center gap-2 flex-none mb-2 px-0.5">
        <span className={`h-2.5 w-2.5 rounded-full ${estilo.ponto}`} />
        <span className={`font-bold uppercase tracking-wide ${estilo.titulo}`}>{titulo}</span>
        <span className={`text-2xl font-bold tabular-nums ml-auto ${estilo.titulo}`}>{tickets.length}</span>
      </div>
      <div className="text-[11px] text-gray-400 -mt-2 mb-2 px-0.5 flex-none">{faixa}</div>
      <div className="xl:flex-1 xl:min-h-0">
        <ListaTickets tickets={tickets} thresh={THRESH_ATENDIMENTO} vazio="Nenhum" />
      </div>
    </div>
  );
};

// Lista com rolagem automática: TV não tem mouse, então quando não cabe ela desce
// devagar, para um pouco no fim e volta ao topo.
const RolagemAutomatica: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const PIXELS_POR_SEGUNDO = 25;
    const PAUSA_MS = 4000;
    let frame = 0;
    let fase: 'topo' | 'descendo' | 'fim' = 'topo';
    let faseDesde = performance.now();
    let ultimo = faseDesde;
    let posicao = 0;
    const irPara = (nova: typeof fase, t: number) => {
      fase = nova;
      faseDesde = t;
    };
    const passo = (t: number) => {
      const dt = (t - ultimo) / 1000;
      ultimo = t;
      const sobra = el.scrollHeight - el.clientHeight;
      if (sobra <= 2) {
        // Tudo cabe: fica parado no topo
        posicao = 0;
        el.scrollTop = 0;
        irPara('topo', t);
      } else if (fase === 'topo' && t - faseDesde >= PAUSA_MS) {
        irPara('descendo', t);
      } else if (fase === 'descendo') {
        posicao = Math.min(sobra, posicao + PIXELS_POR_SEGUNDO * dt);
        el.scrollTop = posicao;
        if (posicao >= sobra) irPara('fim', t);
      } else if (fase === 'fim' && t - faseDesde >= PAUSA_MS) {
        posicao = 0;
        el.scrollTop = 0;
        irPara('topo', t);
      }
      frame = requestAnimationFrame(passo);
    };
    frame = requestAnimationFrame(passo);
    return () => cancelAnimationFrame(frame);
  }, []);
  return (
    <div ref={ref} className="xl:h-full xl:overflow-hidden">
      {children}
    </div>
  );
};

const CanalIcon: React.FC<{ canal: CanalTicket }> = ({ canal }) => {
  if (canal === 'META') return <Mail className="h-3.5 w-3.5" />;
  if (canal === 'DESCONHECIDO') return <Phone className="h-3.5 w-3.5" />;
  return <MessageCircle className="h-3.5 w-3.5" />;
};

const ListaTickets: React.FC<{ tickets: PainelTicket[]; thresh: { warn: number; crit: number }; vazio: string }> = ({ tickets, thresh, vazio }) => {
  if (tickets.length === 0) {
    return (
      <div className="text-center text-sm text-gray-500 border border-dashed border-zinc-700 rounded-lg py-6">{vazio}</div>
    );
  }
  return (
    <RolagemAutomatica>
      <div className="flex flex-col gap-1.5">
        {tickets.map((t) => (
          <CardTicket key={t.id} ticket={t} thresh={thresh} />
        ))}
      </div>
    </RolagemAutomatica>
  );
};

const CardTicket: React.FC<{ ticket: PainelTicket; thresh: { warn: number; crit: number } }> = ({ ticket, thresh }) => {
  const elapsed = decorrido(ticket);
  const pausado = !!ticket.aguardandoClienteDesde;
  const urg = urgenciaTicket(ticket, thresh);
  return (
    <div className={`bg-zinc-800/70 border-l-4 ${pausado ? 'border-l-sky-400' : URGENCY_BORDER[urg]} border border-zinc-700/70 rounded-md px-3 py-2`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-sm sm:text-base truncate">{ticket.contato?.nome || ticket.contato?.telefone || 'Sem nome'}</span>
        <span className={`text-sm sm:text-base font-bold tabular-nums flex items-center gap-1.5 flex-none ${pausado ? 'text-sky-400' : URGENCY_TEXT[urg]}`}>
          {pausado && <PauseCircle className="h-4 w-4" />}
          {!pausado && urg === 'crit' && <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />}
          {fmtElapsed(elapsed)}
        </span>
      </div>
      <div className="flex items-center gap-2 mt-1 min-w-0">
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-zinc-700 text-gray-300 px-1.5 py-0.5 rounded flex-none">
          <CanalIcon canal={ticket.canal} />
          {CANAL_LABEL[ticket.canal]}
        </span>
        {pausado && (
          <span className="inline-flex items-center text-[11px] font-semibold bg-sky-500/15 text-sky-300 px-1.5 py-0.5 rounded flex-none">
            Aguardando cliente
          </span>
        )}
        {/* Fila some só no tablet (md), onde as 3 colunas ficam estreitas */}
        {ticket.fila?.nome && <span className="text-xs text-gray-400 truncate md:hidden lg:inline">{ticket.fila.nome}</span>}
        {ticket.atendente && (
          <span className="ml-auto flex items-center gap-1.5 text-sm text-gray-300 flex-none">
            <Avatar className="h-5 w-5">
              <AvatarImage src={ticket.atendente.avatarUrl ? getFileUrl(ticket.atendente.avatarUrl) : undefined} />
              <AvatarFallback className="text-[9px]">{getInitials(ticket.atendente.nome)}</AvatarFallback>
            </Avatar>
            {ticket.atendente.nome.split(' ')[0]}
          </span>
        )}
      </div>
    </div>
  );
};

const ListaEquipe: React.FC<{ atendentes: Omit<AtendenteCarga, 'email'>[] }> = ({ atendentes }) => {
  if (atendentes.length === 0) {
    return <div className="text-center text-sm text-gray-500 py-6">Nenhum atendente ativo.</div>;
  }
  return (
    <RolagemAutomatica>
      <div className="flex flex-col">
        <div className="flex items-center text-[11px] uppercase tracking-wide text-gray-500 font-semibold px-1 pb-1.5 border-b border-zinc-800">
          <span>Atendente</span>
          <span className="ml-auto">Chats</span>
        </div>
        {atendentes.map((a) => {
          const status = a.status || 'offline';
          return (
            <div key={a.id} className={`flex items-center gap-2.5 px-1 py-2 border-b border-zinc-800/70 ${status === 'offline' ? 'opacity-50' : ''}`}>
              <span className="relative flex-none">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={a.avatarUrl ? getFileUrl(a.avatarUrl) : undefined} />
                  <AvatarFallback className="text-[10px]">{getInitials(a.nome)}</AvatarFallback>
                </Avatar>
                <span className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-zinc-900 ${STATUS_DOT[status]}`} />
              </span>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{a.nome}</div>
                <div className="text-[11px] text-gray-500">{STATUS_LABEL[status]}</div>
              </div>
              <span className={`ml-auto inline-flex justify-center min-w-[2rem] rounded-md px-1.5 py-0.5 text-base font-bold tabular-nums ${a.ticketsEmAtendimento > 0 ? 'bg-sky-500 text-white' : 'text-gray-500'}`}>
                {a.ticketsEmAtendimento}
              </span>
            </div>
          );
        })}
      </div>
    </RolagemAutomatica>
  );
};

export default PainelTvPage;
